import { ReactNode, useState } from 'react';
import { GitCompareArrows } from 'lucide-react';
import { GatewayCharge, useReconciliationQuery } from '@/client/api/payment/finance';
import { formatMoney } from '@/client/types/payment';
import { RelationshipLink } from '@/client/components/admin/Payment/RelationshipLink';
import { EmptyRow, ErrorNote, FinancePage, card, fmtDate, selectCls, td, th } from './shell';

const Section = ({
  title,
  hint,
  count,
  head,
  children,
}: {
  title: string;
  hint: string;
  count: number;
  head: string[];
  children: ReactNode;
}) => (
  <div className={card}>
    <div className="border-b border-gray-100 px-5 py-3">
      <h2 className="text-sm font-semibold text-gray-800">
        {title} <span className="font-normal text-gray-500">· {count}</span>
      </h2>
      <p className="mt-0.5 text-sm text-gray-500">{hint}</p>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-100">
        <thead>
          <tr className="bg-gray-50">
            {head.map((h) => (
              <th key={h} className={th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {count ? children : <EmptyRow cols={head.length}>Nothing to review</EmptyRow>}
        </tbody>
      </table>
    </div>
  </div>
);

const GatewayRow = ({ c }: { c: GatewayCharge }) => (
  <tr>
    <td className={`${td} font-mono`}>{c.reference}</td>
    <td className={`${td} font-semibold tabular-nums`}>{formatMoney(c.amount, c.ccode)}</td>
    <td className={td}>{c.email || '—'}</td>
    <td className={`${td} capitalize`}>{c.channel?.replace('_', ' ') || '—'}</td>
    <td className={`${td} text-gray-500`}>{fmtDate(c.paid_at)}</td>
  </tr>
);

const ReconciliationPage = () => {
  const [days, setDays] = useState(7);
  const { data, isLoading, isFetching, error } = useReconciliationQuery({ days });

  return (
    <FinancePage
      icon={GitCompareArrows}
      title="Reconciliation"
      description="Every successful Paystack charge in the period, matched against our payment records. Wallet top-ups are matched through the wallet ledger. It reads the Paystack account the Live / Test switch points at."
      actions={
        <select
          aria-label="Period"
          className={selectCls}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {[1, 7, 30, 90].map((d) => (
            <option key={d} value={d}>
              Last {d} {d === 1 ? 'day' : 'days'}
            </option>
          ))}
        </select>
      }
    >
      <ErrorNote error={error} />
      {(isLoading || isFetching) && (
        <p className="text-sm text-gray-500">Reading charges from Paystack…</p>
      )}
      {data && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Paystack account', data.mode === 'test' ? 'Test' : 'Live', `${data.gateway_count.toLocaleString()} successful charges`],
              ['Collected at Paystack', formatMoney(data.gateway_total, 'GHS'), `last ${data.days} days`],
              ['Matched', data.matched.toLocaleString(), `of ${data.gateway_count.toLocaleString()}`],
              [
                'Paystack balance',
                data.balance.map((b) => formatMoney(b.balance, b.ccode)).join(' · ') || '—',
                'available now',
              ],
            ].map(([k, v, sub]) => (
              <div key={k} className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                <p className="text-xs text-gray-500">{k}</p>
                <p className="mt-1 text-2xl font-black tabular-nums text-gray-900">{v}</p>
                <p className="mt-1 text-sm text-gray-500">{sub}</p>
              </div>
            ))}
          </div>

          {data.truncated && (
            <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              This period has more charges than one pass reads, so the lists below are incomplete.
              Choose a shorter period.
            </div>
          )}

          <Section
            title="Paid at Paystack, not recorded here"
            hint="The customer was charged but we hold no payment for it. Check the invoice and record or refund it."
            count={data.gateway_only.length}
            head={['Reference', 'Amount', 'Customer', 'Channel', 'Paid']}
          >
            {data.gateway_only.map((c) => (
              <GatewayRow key={c.reference} c={c} />
            ))}
          </Section>

          <Section
            title="Amounts differ"
            hint="Both sides know the charge, for different amounts."
            count={data.amount_mismatch.length}
            head={['Reference', 'Paystack', 'Recorded here', 'Payment', 'Paid']}
          >
            {data.amount_mismatch.map((c) => (
              <tr key={c.reference}>
                <td className={`${td} font-mono`}>{c.reference}</td>
                <td className={`${td} font-semibold tabular-nums`}>{formatMoney(c.amount, c.ccode)}</td>
                <td className={`${td} tabular-nums`}>{formatMoney(c.local_amount ?? 0, c.ccode)}</td>
                <td className={td}>{c.pid != null && <RelationshipLink kind="payment" id={c.pid} />}</td>
                <td className={`${td} text-gray-500`}>{fmtDate(c.paid_at)}</td>
              </tr>
            ))}
          </Section>

          <Section
            title="Recorded as paid here, no matching charge at Paystack"
            hint="We marked these paid by Paystack, but Paystack reports no successful charge with that reference in the period."
            count={data.local_only.length}
            head={['Payment', 'Reference', 'Amount', 'Recorded']}
          >
            {data.local_only.map((p) => (
              <tr key={p.pid}>
                <td className={td}>
                  <RelationshipLink kind="payment" id={p.pid} />
                </td>
                <td className={`${td} font-mono`}>{p.reference}</td>
                <td className={`${td} font-semibold tabular-nums`}>{formatMoney(p.amount, p.ccode)}</td>
                <td className={`${td} text-gray-500`}>{fmtDate(p.date_added)}</td>
              </tr>
            ))}
          </Section>
        </>
      )}
    </FinancePage>
  );
};

export default ReconciliationPage;
